import _ from 'lodash';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { FlowItem } from '../../../../_components/flow-builder/TypeDefs';
import { journeyCanvasConstants } from '../../../../constants/JourneyCanvas';
import { JourneyCanvasState, SidebarState } from '../../utility/TypeDefs';
import { CemSaveBtnEnablePayload, DesignCompensatoryEventMapPayload, SaveCompensatoryEventMapPayload } from '../TypeDefs';

import createCem from '../../../../assets/images/CreateCem.svg';
import { getFlowItemButtons } from '../../utility/helpers';

/**
 * @method designCompensatoryEventMap
 * @description Design compensatory event map flow items and connectors
 * @param state Journey canvas state
 * @param parentNodeId node Id for parent flow item
 * @returns parentJourney, compensatoryEventMaps and set designingCEM to true
 */
export const designCompensatoryEventMap = (state: JourneyCanvasState, {
  parentNodeId,
  compensatoryEventMap
}: DesignCompensatoryEventMapPayload): JourneyCanvasState => {
  const parentFlowItem = state.flowItems.find((fi) => fi.nodeId === parentNodeId);
  if (!parentFlowItem || !state.flowItemDataMap[parentNodeId].ingressEvent) {
    return state;
  }
  const copiedParentFlowItem: FlowItem = {
    ...parentFlowItem,
    revision: (parentFlowItem.revision ?? 10) + 1, // change revision to reflect the new position,
    config: {
      ...parentFlowItem.config,
      orientation: Orientation.VERTICAL,
      draggable: false,
    },
    buttons: [],
    isRoot: true,
    isStackedStyled: false,
    outgoingIcon: {
      src: createCem,
      height: journeyCanvasConstants.cemRootItemIconSize,
      width: journeyCanvasConstants.cemRootItemIconSize * (17 / 11), // very specific based on svg size
    },
  };
  // For an existing CEM (already has flow items) the picker should stay closed on entry
  // so the user just sees the saved canvas. The slide-animation useEffect will dispatch
  // LOAD_COMPENSATORY_EVENT_MAP ~700ms later which also resolves to NONE in that case;
  // matching the initial state here prevents the picker from flashing open during the
  // animation. For a new CEM the picker should be open so the user can pick the first
  // service.
  const hasExistingCem = !!compensatoryEventMap?.flowItems?.length;
  return {
    ...state,
    // copy parent flow builder
    parentJourney: {
      flowConnectors: state.flowConnectors,
      flowItems: state.flowItems,
      ingressNodeId: state.ingressNodeId,
      isRootExternal: state.isRootExternal
    },
    compensatoryEventMap: compensatoryEventMap ?? {
      flowConnectors: [],
      flowItems: [],
      ingressNodeId: ''
    },
    initialCemData: compensatoryEventMap,
    initialCemCondExprData: _.cloneDeep(state.flowItemDataMap),
    flowItems: [copiedParentFlowItem],
    flowConnectors: [],
    payloadTransformations: compensatoryEventMap?.payloadTransformations ?? [],
    cemRootNodeId: parentNodeId,
    designingCEM: true,
    cemRootNodeName: parentFlowItem.name,
    selectedFlowItem: undefined,
    sidebarState: hasExistingCem ? SidebarState.NONE : SidebarState.ASSOCIATED_SERVICES,
    isRootExternal: false,
    cemSaveBtn: false,
  };
};

/**
 * @method loadCompensatoryEventMap
 * @description load compensatory event map flow items and connectors
 * @param state Journey canvas state
 * @returns compensatory event map flow items and connectors
 */
export const loadCompensatoryEventMap = (state: JourneyCanvasState): JourneyCanvasState => {
  const hasItems = !!state.compensatoryEventMap?.flowItems.length;
  // If the CEM entry animation finishes after the user selects a flow item,
  // preserve their current sidebarState so the service picker doesn't flicker
  // closed. We only apply the default initial sidebar state before the user has
  // selected a node in CEM.
  const userHasSelectedNode = !!state.selectedFlowItem;
  return {
    ...state,
    flowItems: [{
      ...state.flowItems[0],
      x: journeyCanvasConstants.rootExternalItemPosition.x,
      y: journeyCanvasConstants.rootExternalItemPosition.y,
    }, ..._.cloneDeep(state.compensatoryEventMap?.flowItems ?? [])],
    flowConnectors: _.cloneDeep(state.compensatoryEventMap?.flowConnectors ?? []),
    ingressNodeId: state.compensatoryEventMap?.ingressNodeId ?? '',
    sidebarState: userHasSelectedNode
      ? state.sidebarState
      : (hasItems ? SidebarState.NONE : SidebarState.ASSOCIATED_SERVICES),
  };
};

/**
 * @method cancelCompensatoryEventMap
 * @description no changes are done in compensatory event map
 * @param state Journey canvas state
 * @returns flowItems, flowConnectors, ingressNodeId, cemRootNodeId, selectedFlowItem and sidebarState
 */
export const cancelCompensatoryEventMap = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  flowItems: [state.flowItems[0]],
  flowConnectors: [],
  ingressNodeId: state.parentJourney?.ingressNodeId ?? '',
  cemRootNodeId: undefined,
  selectedFlowItem: undefined,
  sidebarState: SidebarState.NONE,
  initialCemCondExprData: undefined,
});

/**
 * @method saveCompensatoryEventMap
 * @description Save compensatory eventMap
 * @param state Journey canvas state
 * @param compensatoryEventMap compensatoryEventMap name, nodes and edges
 * @returns Compensatory event maps
 */
export const saveCompensatoryEventMap = (state: JourneyCanvasState, {
  itemDataMap, nodeId, cemName
}: SaveCompensatoryEventMapPayload): JourneyCanvasState => {
  if (cemName) {
    state.flowItemDataMap[nodeId].compensatoryEventMapName = cemName;
  }
  return {
    ...state,
    parentJourney: {
      ingressNodeId: state.parentJourney?.ingressNodeId ?? '',
      flowItems: state.parentJourney?.flowItems?.map((f) => {
        if (f.nodeId === nodeId) {
          f.isStackedStyled = true;
          return f;
        }
        return f;
      }) ?? [],
      isRootExternal: state.isRootExternal,
      flowConnectors: state.parentJourney?.flowConnectors ?? []
    },
    flowItems: [state.flowItems[0]],
    flowConnectors: [],
    ingressNodeId: state.parentJourney?.ingressNodeId ?? '',
    cemRootNodeId: undefined,
    selectedFlowItem: undefined,
    sidebarState: SidebarState.NONE,
    flowItemDataMap: {
      ...state.flowItemDataMap,
      ...itemDataMap
    },
    stepItems: state.stepItems.map((si) => {
      si.cemCount = state.stepJourneyDataMap[si.nodeId].flowItems.filter((fi) => !!state.flowItemDataMap[fi.nodeId]?.compensatoryEventMapName).length;
      return si;
    })
  };
};

/**
 * @method restoreParentJourney
 * @description restore dat of the parent journey
 * @param state Journey canvas state
 * @returns return back to parent journey
 */
export const restoreParentJourney = (state: JourneyCanvasState): JourneyCanvasState => {
  const flowItem = state.parentJourney?.flowItems?.find((fi) => fi.nodeId === state.flowItems[0].nodeId);
  if (flowItem) {
    const hasCEM = !!state.flowItemDataMap[flowItem.nodeId]?.compensatoryEventMapName;
    flowItem.buttons = getFlowItemButtons(hasCEM, state.flowItemDataMap[flowItem.nodeId].isCompensateAction, state.showTransformPayloadModal, !!state.viewJourneyInitiated);
    flowItem.isStackedStyled = hasCEM;
  }
  return {
    ...state,
    flowItems: state.parentJourney?.flowItems ?? [],
    flowConnectors: state.parentJourney?.flowConnectors ?? [],
    designingCEM: false,
    isRootExternal: state.parentJourney?.isRootExternal ?? false,
    initialCemCondExprData: undefined,
  };
};

/**
 * @method deleteCompensatoryEventMap
 * @description delete the compensatory event map
 * @param state Journey canvas state
 * @returns updated state of compensatory event map
 */
export const deleteCompensatoryEventMap = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  flowItems: [state.flowItems[0]],
  flowConnectors: [],
  ingressNodeId: state.parentJourney?.ingressNodeId ?? '',
  flowItemDataMap: {
    ...state.flowItemDataMap,
    [state.cemRootNodeId ?? '']: {
      ...state.flowItemDataMap[state.cemRootNodeId ?? ''],
      compensatoryEventMapName: undefined
    }
  },
  cemRootNodeId: undefined,
  showNotifyDialog: false,
  selectedFlowItem: undefined,
  sidebarState: SidebarState.NONE,
  stepItems: state.stepItems.map((si) => {
    si.cemCount = state.stepJourneyDataMap[si.nodeId].flowItems.filter((fi) => !!state.flowItemDataMap[fi.nodeId]?.compensatoryEventMapName).length;
    return si;
  }),
});

/**
 * @method cemSaveBtnEnable
 * @description Enables or disables the CEM save button.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @param {CemSaveBtnEnablePayload} payload - The payload containing the saveCem value.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const cemSaveBtnEnable = (state: JourneyCanvasState, {
  saveCem
}: CemSaveBtnEnablePayload): JourneyCanvasState => ({
  ...state,
  cemSaveBtn: saveCem
});
